import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { initialState } from "./initialState";
import {
  IGeneralSettingsState,
  IProductAnalyzerSettingsState,
  ITag,
} from "./settings.types";
import {
  createTagThunk,
  deleteTagThunk,
  getSettingsThunk,
  getTagsThunk,
  updateGeneralSettingsThunk,
  updateProductAnalyzerSettingsThunk,
  updateTagThunk,
} from "./settings.thunk";
import { TGetSettingsResponse } from "../../services/apiV2.types";

export const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateGeneralSettings: (
      state,
      action: PayloadAction<Partial<IGeneralSettingsState>>,
    ) => {
      state.general = { ...state.general, ...action.payload };
    },
    updateProductAnalyzerSettings: (
      state,
      action: PayloadAction<Partial<IProductAnalyzerSettingsState>>,
    ) => {
      state.productAnalyzer = { ...state.productAnalyzer, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Get tags
    builder.addCase(
      getTagsThunk.fulfilled,
      (state, action: PayloadAction<ITag[]>) => {
        state.productAnalyzer.tags = [...action.payload];
      },
    );

    // Create new tag
    builder.addCase(
      createTagThunk.fulfilled,
      (state, action: PayloadAction<ITag>) => {
        state.productAnalyzer.tags = [
          ...state.productAnalyzer.tags,
          action.payload,
        ];
      },
    );

    // Update tag
    builder.addCase(updateTagThunk.fulfilled, (state, action) => {
      const index = state.productAnalyzer.tags.findIndex(
        (tag) => tag.id === action.meta.arg.id,
      );
      state.productAnalyzer.tags[index] = {
        ...state.productAnalyzer.tags[index],
        caption: action.meta.arg.caption,
      };
    });

    // Delete tag
    builder.addCase(deleteTagThunk.fulfilled, (state, action) => {
      const index = state.productAnalyzer.tags.findIndex(
        (tag) => tag.id === action.meta.arg,
      );
      if (index >= 0) state.productAnalyzer.tags.splice(index, 1);
    });

    // Get settings
    builder.addCase(
      getSettingsThunk.fulfilled,
      (state, action: PayloadAction<TGetSettingsResponse>) => {
        const {
          inboundShipping,
          inboundPlacement,
          storageTime,
          fulfilment,
          prepFee,
          miscFee,
          miscFeePercent,
          autoOpenExtension,
          recentLookupsClearedAt,
          minBsr,
          maxBsr,
          minProfit,
          minRoi,
          defaultRanksAndPricesTimeFrame,
          defaultBbAnalysisTimeFrame,
          defaultFbmCost,
          defaultCustomRoiCalc,
          showProfitInQuickInfo,
          showProfitMarginInQuickInfo,
          showRoiInQuickInfo,
          showBreakevenInQuickInfo,
          showOfferSummaryInQuickInfo,
          showAveragePricingInQuickInfo,
          defaultAverageTimeFrameInQuickInfo,
          defaultBBAverageTimeFrameInQuickInfo,
          showPricesChart,
          showSoldChart,
          showOffersChart,
          defaultChartTimeSpan,
          panelOrder,
        } = action.payload;
        state.general = {
          ...state.general,
          inboundShipping,
          inboundPlacement,
          storageTime,
          fulfilment,
          prepFee,
          miscFee,
          miscFeePercent,
          autoOpenExtension,
          recentLookupsClearedAt,
        };
        state.productAnalyzer = {
          ...state.productAnalyzer,
          minBsr,
          maxBsr,
          minProfit,
          minRoi,
          defaultRanksAndPricesTimeFrame,
          defaultBbAnalysisTimeFrame,
          defaultFbmCost,
          defaultCustomRoiCalc,
          showProfitInQuickInfo,
          showProfitMarginInQuickInfo,
          showRoiInQuickInfo,
          showBreakevenInQuickInfo,
          showOfferSummaryInQuickInfo,
          showAveragePricingInQuickInfo,
          defaultAverageTimeFrameInQuickInfo,
          defaultBBAverageTimeFrameInQuickInfo,
          showPricesChart,
          showSoldChart,
          showOffersChart,
          defaultChartTimeSpan: defaultChartTimeSpan ?? state.productAnalyzer.defaultChartTimeSpan,
          panelOrder,
        };
      },
    );

    // Update settings
    builder.addCase(updateGeneralSettingsThunk.pending, (state, action) => {
      state.general = { ...state.general, ...action.meta.arg };
    });
    builder.addCase(updateProductAnalyzerSettingsThunk.pending, (state, action) => {
      state.productAnalyzer = { ...state.productAnalyzer, ...action.meta.arg };
    });
  },
});

export const { updateGeneralSettings, updateProductAnalyzerSettings } =
  settingsSlice.actions;

export default settingsSlice.reducer;
